import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Server,
  Shield,
  Upload,
  FileText,
  AlertCircle,
  Activity,
} from 'lucide-react';

interface ServerCreationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  loading?: boolean;
}

// Styled components matching Devices pattern
const Button = ({
  children,
  onClick,
  disabled = false,
  variant = 'default',
  size = 'default',
  className = '',
  type = 'button',
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: string;
  size?: string;
  className?: string;
  type?: 'button' | 'submit';
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    default:
      'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800 focus:ring-blue-500',
    outline:
      'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-blue-500',
    ghost:
      'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-blue-500',
    destructive:
      'bg-red-600 dark:bg-red-700 text-white hover:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500',
  };

  const sizeClasses = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-1.5 text-xs',
    lg: 'px-6 py-3 text-base',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant as keyof typeof variantClasses] || variantClasses.default} ${sizeClasses[size as keyof typeof sizeClasses] || sizeClasses.default} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({
  className = '',
  ...props
}: {
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'>) => {
  return (
    <input
      className={`flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-300 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
};

const Textarea = ({
  className = '',
  ...props
}: {
  className?: string;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'>) => {
  return (
    <textarea
      className={`flex w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-300 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 font-mono ${className}`}
      {...props}
    />
  );
};

const ServerCreationModal: React.FC<ServerCreationModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation(['Organization']);
  const [serverAeTitle, setServerAeTitle] = useState('');
  const [serverHost, setServerHost] = useState('');
  const [serverPort, setServerPort] = useState(11112);
  const [timeout, setTimeout] = useState(30);

  // DICOM Capabilities
  const [supportsCStore, setSupportsCStore] = useState(true);
  const [supportsCEcho, setSupportsCEcho] = useState(true);
  const [supportsQueryRetrieve, setSupportsQueryRetrieve] = useState(true);
  const [queryLevelPatient, setQueryLevelPatient] = useState(true);
  const [queryLevelStudy, setQueryLevelStudy] = useState(true);
  const [queryLevelSeries, setQueryLevelSeries] = useState(true);
  const [queryLevelImage, setQueryLevelImage] = useState(false);

  const [tlsEnabled, setTlsEnabled] = useState(false);
  const [tlsRequireClientAuth, setTlsRequireClientAuth] = useState(false);
  const [certFormat, setCertFormat] = useState<'individual' | 'pfx'>(
    'individual'
  );
  const [certInputMode, setCertInputMode] = useState<'text' | 'file'>('text');

  const [tlsCaCert, setTlsCaCert] = useState('');
  const [tlsServerCert, setTlsServerCert] = useState('');
  const [tlsServerKey, setTlsServerKey] = useState('');
  const [tlsClientCert, setTlsClientCert] = useState('');
  const [tlsClientKey, setTlsClientKey] = useState('');

  const [pfxFile, setPfxFile] = useState('');
  const [pfxPassword, setPfxPassword] = useState('');
  const [pfxFileName, setPfxFileName] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setter(content);
      };
      reader.readAsText(file);
    }
  };

  const handlePfxFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPfxFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
        const base64 = window.btoa(binary);
        setPfxFile(base64);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!serverAeTitle.trim()) {
      newErrors.serverAeTitle = t('servers.validation.aeTitleRequired');
    } else if (serverAeTitle.length > 16) {
      newErrors.serverAeTitle = t('servers.validation.aeTitleMaxLength');
    } else if (!/^[A-Z0-9_-]+$/.test(serverAeTitle)) {
      newErrors.serverAeTitle = t('servers.validation.aeTitleFormat');
    }

    if (!serverHost.trim()) {
      newErrors.serverHost = t('servers.validation.hostRequired');
    }

    if (!serverPort || serverPort < 1 || serverPort > 65535) {
      newErrors.serverPort = t('servers.validation.portRange');
    }

    if (tlsEnabled) {
      if (certFormat === 'pfx') {
        if (!pfxFile) {
          newErrors.pfxFile = t('servers.validation.pfxRequired');
        }
      } else {
        if (!tlsCaCert.trim()) {
          newErrors.tlsCaCert = t('servers.validation.caCertRequired');
        }

        if (tlsRequireClientAuth) {
          if (!tlsClientCert.trim()) {
            newErrors.tlsClientCert = t('servers.validation.clientCertRequired');
          }
          if (!tlsClientKey.trim()) {
            newErrors.tlsClientKey = t('servers.validation.clientKeyRequired');
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const values = {
      server_ae_title: serverAeTitle.trim(),
      server_host: serverHost.trim(),
      server_port: serverPort,
      timeout: timeout || 30,
      supports_c_store: supportsCStore,
      supports_c_echo: supportsCEcho,
      supports_query_retrieve: supportsQueryRetrieve,
      query_level_patient: queryLevelPatient,
      query_level_study: queryLevelStudy,
      query_level_series: queryLevelSeries,
      query_level_image: queryLevelImage,
      tls_enabled: tlsEnabled,
      tls_require_client_auth: tlsRequireClientAuth,
      cert_format: tlsEnabled ? certFormat : null,
      tls_ca_cert:
        tlsEnabled && certFormat === 'individual' && tlsCaCert.trim()
          ? tlsCaCert.trim()
          : null,
      tls_server_cert:
        tlsEnabled && certFormat === 'individual' && tlsServerCert.trim()
          ? tlsServerCert.trim()
          : null,
      tls_server_key:
        tlsEnabled && certFormat === 'individual' && tlsServerKey.trim()
          ? tlsServerKey.trim()
          : null,
      tls_client_cert:
        tlsEnabled &&
        certFormat === 'individual' &&
        tlsRequireClientAuth &&
        tlsClientCert.trim()
          ? tlsClientCert.trim()
          : null,
      tls_client_key:
        tlsEnabled &&
        certFormat === 'individual' &&
        tlsRequireClientAuth &&
        tlsClientKey.trim()
          ? tlsClientKey.trim()
          : null,
      pfx_file: tlsEnabled && certFormat === 'pfx' && pfxFile ? pfxFile : null,
      pfx_password:
        tlsEnabled && certFormat === 'pfx' && pfxPassword ? pfxPassword : null,
    };

    onSubmit(values);
  };

  const handleCancel = () => {
    // Reset all state
    setServerAeTitle('');
    setServerHost('');
    setServerPort(11112);
    setTimeout(30);
    setSupportsCStore(true);
    setSupportsCEcho(true);
    setSupportsQueryRetrieve(true);
    setQueryLevelPatient(true);
    setQueryLevelStudy(true);
    setQueryLevelSeries(true);
    setQueryLevelImage(false);
    setTlsEnabled(false);
    setTlsRequireClientAuth(false);
    setCertFormat('individual');
    setCertInputMode('text');
    setTlsCaCert('');
    setTlsServerCert('');
    setTlsServerKey('');
    setTlsClientCert('');
    setTlsClientKey('');
    setPfxFile('');
    setPfxPassword('');
    setPfxFileName('');
    setErrors({});
    onCancel();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Server className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('servers.modal.createTitle')}
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="space-y-5">
            {/* Basic Configuration - 2 Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Server AE Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('servers.columnAeTitle')} <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={serverAeTitle}
                  onChange={(e) =>
                    setServerAeTitle(e.target.value.toUpperCase())
                  }
                  placeholder={t('servers.modal.aeTitlePlaceholder')}
                  maxLength={16}
                  className={errors.serverAeTitle ? 'border-red-500' : ''}
                />
                {errors.serverAeTitle && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.serverAeTitle}
                  </p>
                )}
              </div>

              {/* Server Host */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('servers.columnHost')} <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={serverHost}
                  onChange={(e) => setServerHost(e.target.value)}
                  placeholder={t('servers.modal.hostPlaceholder')}
                  className={errors.serverHost ? 'border-red-500' : ''}
                />
                {errors.serverHost && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.serverHost}
                  </p>
                )}
              </div>

              {/* Server Port */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('servers.columnPort')} <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  value={serverPort}
                  onChange={(e) => setServerPort(parseInt(e.target.value))}
                  placeholder={t('servers.modal.portPlaceholder')}
                  min={1}
                  max={65535}
                  className={errors.serverPort ? 'border-red-500' : ''}
                />
                {errors.serverPort && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.serverPort}
                  </p>
                )}
              </div>

              {/* Connection Timeout */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('servers.modal.timeout')}
                </label>
                <Input
                  type="number"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value))}
                  min={5}
                  max={300}
                  placeholder={t('servers.modal.timeoutPlaceholder')}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700"></div>

            {/* DICOM Capabilities Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-4 w-4 text-green-600 dark:text-green-400" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('servers.modal.dicomCapabilities')}
                </h3>
              </div>

              {/* DICOM Service Operations - 3 Column Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {/* C-STORE Support */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <label className="text-xs font-medium text-gray-900 dark:text-white block">
                      C-STORE
                    </label>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Store images
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSupportsCStore(!supportsCStore)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      supportsCStore
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        supportsCStore ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* C-ECHO Support */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <label className="text-xs font-medium text-gray-900 dark:text-white block">
                      C-ECHO
                    </label>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Verify connection
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSupportsCEcho(!supportsCEcho)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      supportsCEcho
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        supportsCEcho ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Query/Retrieve Support */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <label className="text-xs font-medium text-gray-900 dark:text-white block">
                      C-FIND/C-MOVE
                    </label>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Query/Retrieve
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSupportsQueryRetrieve(!supportsQueryRetrieve)
                    }
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      supportsQueryRetrieve
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        supportsQueryRetrieve
                          ? 'translate-x-5'
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Query Levels */}
              {supportsQueryRetrieve && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Query Levels
                  </label>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {/* Patient Level */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={queryLevelPatient}
                        onChange={(e) => setQueryLevelPatient(e.target.checked)}
                        className="text-green-600 focus:ring-green-500 rounded"
                      />
                      <span className="text-xs text-gray-900 dark:text-white">
                        PATIENT
                      </span>
                    </label>

                    {/* Study Level */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={queryLevelStudy}
                        onChange={(e) => setQueryLevelStudy(e.target.checked)}
                        className="text-green-600 focus:ring-green-500 rounded"
                      />
                      <span className="text-xs text-gray-900 dark:text-white">
                        STUDY
                      </span>
                    </label>

                    {/* Series Level */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={queryLevelSeries}
                        onChange={(e) => setQueryLevelSeries(e.target.checked)}
                        className="text-green-600 focus:ring-green-500 rounded"
                      />
                      <span className="text-xs text-gray-900 dark:text-white">
                        SERIES
                      </span>
                    </label>

                    {/* Image Level */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={queryLevelImage}
                        onChange={(e) => setQueryLevelImage(e.target.checked)}
                        className="text-green-600 focus:ring-green-500 rounded"
                      />
                      <span className="text-xs text-gray-900 dark:text-white">
                        IMAGE
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700"></div>

            {/* TLS/SSL Section Header */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  TLS/SSL Configuration
                </h3>
              </div>

              {/* TLS Enable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3">
                <div>
                  <label className="text-xs font-medium text-gray-900 dark:text-white">
                    Enable TLS/SSL
                  </label>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Encrypt DICOM communications
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTlsEnabled(!tlsEnabled)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    tlsEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      tlsEnabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {tlsEnabled && (
              <div className="space-y-3">
                {/* Certificate Format Selection */}
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className={`flex items-center space-x-2 rounded-lg border p-2 cursor-pointer transition-colors ${
                      certFormat === 'individual'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setCertFormat('individual')}
                  >
                    <input
                      type="radio"
                      value="individual"
                      checked={certFormat === 'individual'}
                      onChange={() => setCertFormat('individual')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-1.5">
                      <FileText className="h-3 w-3 text-blue-600" />
                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                        Individual PEM
                      </span>
                    </div>
                  </div>

                  <div
                    className={`flex items-center space-x-2 rounded-lg border p-2 cursor-pointer transition-colors ${
                      certFormat === 'pfx'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setCertFormat('pfx')}
                  >
                    <input
                      type="radio"
                      value="pfx"
                      checked={certFormat === 'pfx'}
                      onChange={() => setCertFormat('pfx')}
                      className="text-blue-600"
                    />
                    <div className="flex items-center gap-1.5">
                      <Upload className="h-3 w-3 text-blue-600" />
                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                        PFX/PKCS12
                      </span>
                    </div>
                  </div>
                </div>

                {certFormat === 'pfx' ? (
                  /* PFX Certificate Upload */
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-3 space-y-3">
                    {/* PFX File Upload */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        PFX/PKCS12 File <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="file"
                        accept=".pfx,.p12,.pkcs12"
                        onChange={handlePfxFileUpload}
                        className={errors.pfxFile ? 'border-red-500' : ''}
                      />
                      {pfxFile && pfxFileName && (
                        <div className="p-1.5 mt-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-[10px] text-green-700 dark:text-green-400">
                          ✓ {pfxFileName}
                        </div>
                      )}
                      {errors.pfxFile && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.pfxFile}
                        </p>
                      )}
                    </div>

                    {/* PFX Password */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        PFX Password{' '}
                        <span className="text-gray-400">(optional)</span>
                      </label>
                      <Input
                        type="password"
                        value={pfxPassword}
                        onChange={(e) => setPfxPassword(e.target.value)}
                        placeholder={t('servers.modal.pfxPasswordPlaceholder')}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                ) : (
                  /* Individual Certificates Section */
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 space-y-3">
                    {/* Certificate Input Mode Selection */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div
                        className={`flex items-center space-x-2 rounded-lg border p-2 cursor-pointer transition-colors ${
                          certInputMode === 'text'
                            ? 'border-blue-500 bg-white dark:bg-gray-800'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                        onClick={() => setCertInputMode('text')}
                      >
                        <input
                          type="radio"
                          value="text"
                          checked={certInputMode === 'text'}
                          onChange={() => setCertInputMode('text')}
                          className="text-blue-600"
                        />
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3 w-3 text-blue-600" />
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            Paste Text
                          </span>
                        </div>
                      </div>

                      <div
                        className={`flex items-center space-x-2 rounded-lg border p-2 cursor-pointer transition-colors ${
                          certInputMode === 'file'
                            ? 'border-blue-500 bg-white dark:bg-gray-800'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                        onClick={() => setCertInputMode('file')}
                      >
                        <input
                          type="radio"
                          value="file"
                          checked={certInputMode === 'file'}
                          onChange={() => setCertInputMode('file')}
                          className="text-blue-600"
                        />
                        <div className="flex items-center gap-1.5">
                          <Upload className="h-3 w-3 text-blue-600" />
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            Upload Files
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* CA Certificate */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        CA Certificate <span className="text-red-500">*</span>
                      </label>

                      {certInputMode === 'text' ? (
                        <Textarea
                          value={tlsCaCert}
                          onChange={(e) => setTlsCaCert(e.target.value)}
                          placeholder={t('servers.modal.certPlaceholder')}
                          rows={3}
                          className={errors.tlsCaCert ? 'border-red-500' : ''}
                        />
                      ) : (
                        <>
                          <Input
                            type="file"
                            accept=".crt,.pem,.cer"
                            onChange={(e) => handleFileUpload(e, setTlsCaCert)}
                            className={errors.tlsCaCert ? 'border-red-500' : ''}
                          />
                          {tlsCaCert && (
                            <div className="p-1.5 mt-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-[10px] text-green-700 dark:text-green-400">
                              ✓ Loaded ({tlsCaCert.length} chars)
                            </div>
                          )}
                        </>
                      )}

                      {errors.tlsCaCert && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.tlsCaCert}
                        </p>
                      )}
                    </div>

                    {/* Server Cert & Key - Grid Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Server Certificate (Optional) */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Server Cert{' '}
                          <span className="text-gray-400">(opt)</span>
                        </label>

                        {certInputMode === 'text' ? (
                          <Textarea
                            value={tlsServerCert}
                            onChange={(e) => setTlsServerCert(e.target.value)}
                            placeholder={t('servers.modal.certBeginPlaceholder')}
                            rows={3}
                          />
                        ) : (
                          <>
                            <Input
                              type="file"
                              accept=".crt,.pem,.cer"
                              onChange={(e) =>
                                handleFileUpload(e, setTlsServerCert)
                              }
                            />
                            {tlsServerCert && (
                              <div className="p-1.5 mt-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-[10px] text-green-700 dark:text-green-400">
                                ✓ Loaded
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Server Private Key (Optional) */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Server Key{' '}
                          <span className="text-gray-400">(opt)</span>
                        </label>

                        {certInputMode === 'text' ? (
                          <Textarea
                            value={tlsServerKey}
                            onChange={(e) => setTlsServerKey(e.target.value)}
                            placeholder={t('servers.modal.keyBeginPlaceholder')}
                            rows={3}
                          />
                        ) : (
                          <>
                            <Input
                              type="file"
                              accept=".key,.pem"
                              onChange={(e) =>
                                handleFileUpload(e, setTlsServerKey)
                              }
                            />
                            {tlsServerKey && (
                              <div className="p-1.5 mt-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-[10px] text-green-700 dark:text-green-400">
                                ✓ Loaded
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Client Authentication */}
                    <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <label className="text-xs font-medium text-gray-900 dark:text-white">
                        Mutual TLS (Client Auth)
                      </label>
                      <button
                        type="button"
                        onClick={() =>
                          setTlsRequireClientAuth(!tlsRequireClientAuth)
                        }
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          tlsRequireClientAuth
                            ? 'bg-green-600'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            tlsRequireClientAuth
                              ? 'translate-x-5'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Client Cert & Key - Grid Layout */}
                    {tlsRequireClientAuth && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Client Certificate */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Client Cert <span className="text-red-500">*</span>
                          </label>

                          {certInputMode === 'text' ? (
                            <Textarea
                              value={tlsClientCert}
                              onChange={(e) => setTlsClientCert(e.target.value)}
                              placeholder={t('servers.modal.certBeginPlaceholder')}
                              rows={3}
                              className={
                                errors.tlsClientCert ? 'border-red-500' : ''
                              }
                            />
                          ) : (
                            <>
                              <Input
                                type="file"
                                accept=".crt,.pem,.cer"
                                onChange={(e) =>
                                  handleFileUpload(e, setTlsClientCert)
                                }
                                className={
                                  errors.tlsClientCert ? 'border-red-500' : ''
                                }
                              />
                              {tlsClientCert && (
                                <div className="p-1.5 mt-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-[10px] text-green-700 dark:text-green-400">
                                  ✓ Loaded
                                </div>
                              )}
                            </>
                          )}

                          {errors.tlsClientCert && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.tlsClientCert}
                            </p>
                          )}
                        </div>

                        {/* Client Private Key */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Client Key <span className="text-red-500">*</span>
                          </label>

                          {certInputMode === 'text' ? (
                            <Textarea
                              value={tlsClientKey}
                              onChange={(e) => setTlsClientKey(e.target.value)}
                              placeholder={t('servers.modal.keyBeginPlaceholder')}
                              rows={3}
                              className={
                                errors.tlsClientKey ? 'border-red-500' : ''
                              }
                            />
                          ) : (
                            <>
                              <Input
                                type="file"
                                accept=".key,.pem"
                                onChange={(e) =>
                                  handleFileUpload(e, setTlsClientKey)
                                }
                                className={
                                  errors.tlsClientKey ? 'border-red-500' : ''
                                }
                              />
                              {tlsClientKey && (
                                <div className="p-1.5 mt-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-[10px] text-green-700 dark:text-green-400">
                                  ✓ Loaded
                                </div>
                              )}
                            </>
                          )}

                          {errors.tlsClientKey && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.tlsClientKey}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800">
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            {t('servers.modal.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                {t('servers.modal.creating')}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Server className="h-4 w-4" />
                {t('servers.create')}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServerCreationModal;
